import { RSAA, ApiError } from 'redux-api-middleware';
import * as Sentry from '@sentry/react';
import backoff from 'redux/helpers/backoff';
import computeApiState from 'redux/helpers/computeApiState';
import getUsageTrackingUrl from 'url-constructors/getUsageTrackingURL';
import { Dispatch } from 'redux';
import { TGetRootState } from 'types/shared';
import { ITopicFilterState } from 'components/TopicFilters/shared';
import getRuntimeEnv from "helpers/env";

const TRACK_FILTER = 'OPUS/USAGE_STATISTICS/FILTER/TRACK_FILTER';

const SAVE_REQUEST = 'OPUS/USAGE_STATISTICS/FILTERS/SAVE_REQUEST';
const SAVE_RETRY = 'OPUS/USAGE_STATISTICS/FILTERS/SAVE_RETRY';
const SAVE_SUCCESS = 'OPUS/USAGE_STATISTICS/FILTERS/SAVE_SUCCESS';
const SAVE_FAILURE = 'OPUS/USAGE_STATISTICS/FILTERS/SAVE_FAILURE';

const INIT_REQUEST = 'OPUS/USAGE_STATISTICS/FILTERS/INIT_REQUEST';
const INIT_SUCCESS = 'OPUS/USAGE_STATISTICS/FILTERS/INIT_SUCCESS';
const INIT_FAILURE = 'OPUS/USAGE_STATISTICS/FILTERS/INIT_FAILURE';

const LOAD_REQUEST = 'OPUS/USAGE_STATISTICS/FILTERS/LOAD_REQUEST';
const LOAD_SUCCESS = 'OPUS/USAGE_STATISTICS/FILTERS/LOAD_SUCCESS';
const LOAD_FAILURE = 'OPUS/USAGE_STATISTICS/FILTERS/LOAD_FAILURE';

type FilterActionTypes =
  | typeof TRACK_FILTER
  | typeof SAVE_REQUEST
  | typeof SAVE_RETRY
  | typeof SAVE_SUCCESS
  | typeof SAVE_FAILURE
  | typeof INIT_REQUEST
  | typeof INIT_SUCCESS
  | typeof INIT_FAILURE
  | typeof LOAD_REQUEST
  | typeof LOAD_SUCCESS
  | typeof LOAD_FAILURE;

interface IFilterActionPayload {
  filterType: keyof ITopicFilterState;
  filterValue: string | number | null;
  filterId?: string;
  recentFilters?: IFilterInfo[];
}

export interface IFilterInfo
  extends Omit<IFilterActionPayload, 'filterId' | 'recentFilters'> {
  usageCount: number;
  lastUsed: string;
  id?: string;
}

interface IFilterAction {
  type: FilterActionTypes;
  payload: IFilterActionPayload;
}

export interface IFilterState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  recentFilters: IFilterInfo[];
}

const initialState: IFilterState = {
  pending: false,
  fulfilled: false,
  rejected: false,
  recentFilters: [],
};

const TRACKING_TYPE = 'filter';
const FILTER_LIMIT = 40;

export default function reducer(
  state: IFilterState = initialState,
  action: IFilterAction
): IFilterState {
  switch (action.type) {
    case TRACK_FILTER: {
      const { filterType, filterValue, filterId } = action.payload;

      /**
       * Find the item in the recentFilters array.
       * We check for both the value and the type, because it might be that
       * a DSP occurs both in streaming and downloads
       */

      const itemIndex = state.recentFilters.findIndex((element) => {
        return (
          element.filterValue === filterValue &&
          element.filterType === filterType
        );
      });

      const isItemInArray = itemIndex > -1;

      // We'll use this intermediary variable to update the store
      let updatedRecentFilters = state.recentFilters;

      if (isItemInArray) {
        updatedRecentFilters = updatedRecentFilters.map((element) =>
          element.filterValue === filterValue &&
          element.filterType === filterType
            ? {
                ...element,
                usageCount: element.usageCount + 1,
                lastUsed: new Date().toISOString(),
              }
            : element
        );
      } else {
        const newFilter: IFilterInfo = {
          filterValue,
          filterType,
          usageCount: 1,
          lastUsed: new Date().toISOString(),
        };

        if (filterId) {
          newFilter.id = filterId;
        }

        updatedRecentFilters.unshift(newFilter);
      }

      /**
       * Sort the entities by date, so the most recent ones are on the top.
       * This should already be OK when inserting new items with unshift,
       * but it might run out of order when we update items with
       * the map function.
       */
      updatedRecentFilters = updatedRecentFilters.sort(
        (a: IFilterInfo, b: IFilterInfo) => {
          const aLastUsed: number = +new Date(a.lastUsed);
          const bLastUsed: number = +new Date(b.lastUsed);

          return bLastUsed - aLastUsed;
        }
      );
      updatedRecentFilters.splice(FILTER_LIMIT); // Cut off everything after the limit we set

      return {
        ...state,
        recentFilters: updatedRecentFilters,
      };
    }

    case SAVE_RETRY: {
      return {
        ...state,
        pending: true,
        fulfilled: false,
      };
    }

    case INIT_SUCCESS:
    case SAVE_SUCCESS: {
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
      };
    }

    case INIT_FAILURE:
    case LOAD_FAILURE:
    case SAVE_FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: true,
      };

    case INIT_REQUEST:
    case LOAD_REQUEST:
    case SAVE_REQUEST:
      return {
        ...state,
        pending: true,
      };

    case LOAD_SUCCESS: {
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        recentFilters: action.payload.recentFilters ?? [],
      };
    }

    default:
      return state;
  }
}

/**
 * This action creator initializes an empty object at a specific component_settings endpoint for a user.
 * Only called once per user, when we get a 404 while trying to load the settings.
 */
function initFilterUsageEndpoint(): (
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any> {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;

    const url = getUsageTrackingUrl(id, TRACKING_TYPE);

    // dispatch the action
    return await dispatch({
      [RSAA]: {
        types: [INIT_REQUEST, INIT_SUCCESS, INIT_FAILURE],
        endpoint: url,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          recentFilters: [],
        }),
      },
    });
  };
}

export const loadRecentFilters = (): ((
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any>) => {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { filterUsage } = getState();
    const apiStatus = computeApiState(filterUsage);

    // bail out if the apiState is other than unloaded
    if (apiStatus !== 'unloaded') {
      return;
    }

    const { id } = getState().authentication.profile;
    const url = getUsageTrackingUrl(id, TRACKING_TYPE);
    // This returns the settings from the API

    try {
      const actionResponse = await dispatch({
        [RSAA]: {
          types: [LOAD_REQUEST, LOAD_SUCCESS, LOAD_FAILURE],
          endpoint: url,
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
        },
      });

      // This should only happen once for every user
      if (actionResponse.error) {
        if ((actionResponse.payload as ApiError<any>).status === 404) {
          return await dispatch(initFilterUsageEndpoint() as any);
        }
      }

      return actionResponse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('There was an error loading your recent filters');
    }
  };
};

/**
 * This action creator reads the store and sends the current state of the store to the KV API
 */
export function saveToApi(): (
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any> {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { id } = getState().authentication.profile;
    const url = getUsageTrackingUrl(id, TRACKING_TYPE);

    const { recentFilters } = getState().filterUsage;

    try {
      const response = await backoff(
        async () =>
          await dispatch({
            [RSAA]: {
              types: [SAVE_REQUEST, SAVE_SUCCESS, SAVE_RETRY],
              endpoint: url,
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ recentFilters }),
            },
          }),
        'An error occurred trying to save your filter usage statistics. Retrying for the %s time...'
      );

      if (response.error) {
        throw response.payload;
      }

      return response;
    } catch (error) {
      // The final retry error will end up here. Log it to console.

      // eslint-disable-next-line no-console
      console.warn('Your recent filters failed to be saved remotely.');
      // eslint-disable-next-line no-console
      console.error(error);

      // Report it to Sentry
      if (getRuntimeEnv().NODE_ENV === 'production') {
        // Send the exception to Sentry
        Sentry.captureException(error);
      }

      // Dispatch a final failed state
      dispatch({ type: SAVE_FAILURE });
    }
  };
}

/**
 * This action creator takes an filterType and filterValue
 * It first updates the local reducer before sending off an API request to sync the local redux store to the API
 *
 * @export
 * @param {string} filterType   The type of the filter you want to track
 * @param {string|number} filterValue  The value of the filter you're tracking
 */
export function trackFilter(
  filterType: keyof ITopicFilterState,
  filterValue: string | number | null,
  filterId?: string
): (dispatch: Dispatch) => Promise<any> {
  return async function (dispatch: Dispatch): Promise<any> {
    await dispatch(loadRecentFilters() as any);
    const payload: IFilterActionPayload = {
      filterType,
      filterValue,
      filterId,
    };

    dispatch({
      type: TRACK_FILTER,
      payload: payload,
    });
    dispatch(saveToApi() as any);
  };
}
